<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">专家遴选</h1>
        <p class="text-sm text-slate-500 mt-1">基于项目领域与指南方向，智能匹配评审专家</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">立项评审</span>
        <span class="px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-full text-xs font-semibold">已完成</span>
      </div>
    </div>

    <!-- Expert Pool -->
    <div class="glass-card rounded-3xl p-6">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">专家池</h2>
        </div>
        <div class="flex items-center gap-4">
          <span class="text-sm text-slate-500">已选择 <span class="font-bold text-blue-600">{{ selectedCount }}</span> 位专家</span>
          <button class="btn-export px-4 py-2 rounded-xl text-white text-sm font-medium flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            导出名单
          </button>
        </div>
      </div>

      <!-- Core Experts -->
      <div class="mb-6">
        <h3 class="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
          <span class="w-2 h-2 rounded-full bg-red-500"></span>
          核心领域专家（契合度 90%+）
        </h3>
        <div class="grid grid-cols-3 gap-4">
          <div v-for="(expert, index) in coreExperts" :key="index" class="expert-card glass-card rounded-2xl p-4 border border-slate-200 cursor-pointer" :class="{ 'is-selected': expert.selected }" @click="expert.selected = !expert.selected">
            <div class="flex items-start gap-3">
              <div class="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                {{ expert.name.charAt(0) }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between">
                  <h4 class="font-bold text-slate-800 text-sm">{{ expert.name }}</h4>
                  <span class="number-font text-lg font-bold text-red-500 score-pulse">{{ expert.match }}%</span>
                </div>
                <p class="text-xs text-slate-500 mt-1">{{ expert.title }} · {{ expert.institution }}</p>
                <p class="text-xs text-slate-400 mt-1 truncate">{{ expert.field }}</p>
                <div class="flex items-center gap-2 mt-2">
                  <span class="px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs">核心</span>
                  <span class="text-xs text-slate-400">{{ expert.projects }} 个项目</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Edge Experts -->
      <div>
        <h3 class="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
          <span class="w-2 h-2 rounded-full bg-amber-500"></span>
          边缘领域专家（契合度 50-60%）
        </h3>
        <div class="grid grid-cols-3 gap-4">
          <div v-for="(expert, index) in edgeExperts" :key="index" class="expert-card glass-card rounded-2xl p-4 border border-slate-200 cursor-pointer" :class="{ 'is-selected': expert.selected }" @click="expert.selected = !expert.selected">
            <div class="flex items-start gap-3">
              <div class="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
                {{ expert.name.charAt(0) }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between">
                  <h4 class="font-bold text-slate-800 text-sm">{{ expert.name }}</h4>
                  <span class="number-font text-lg font-bold text-amber-500">{{ expert.match }}%</span>
                </div>
                <p class="text-xs text-slate-500 mt-1">{{ expert.title }} · {{ expert.institution }}</p>
                <p class="text-xs text-slate-400 mt-1 truncate">{{ expert.field }}</p>
                <div class="flex items-center gap-2 mt-2">
                  <span class="px-2 py-0.5 bg-amber-50 text-amber-600 rounded text-xs">边缘</span>
                  <span class="text-xs text-slate-400">{{ expert.projects }} 个项目</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Selection Reason -->
    <div class="glass-card rounded-3xl p-6">
      <div class="flex items-center gap-2 mb-6">
        <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
          </svg>
        </div>
        <h2 class="text-lg font-bold text-slate-800">智能遴选理由</h2>
      </div>

      <div class="space-y-4">
        <div class="p-4 bg-slate-50 rounded-xl">
          <h4 class="font-semibold text-slate-700 text-sm mb-2">方向契合性</h4>
          <p class="text-xs text-slate-500 leading-relaxed">所选专家的研究方向与项目指南高度契合，核心专家在人工智能领域具有深厚的研究积累，边缘专家可提供跨学科视角。</p>
        </div>
        <div class="p-4 bg-slate-50 rounded-xl">
          <h4 class="font-semibold text-slate-700 text-sm mb-2">学术水平</h4>
          <p class="text-xs text-slate-500 leading-relaxed">专家均为领域内知名学者，具有丰富的项目评审经验和学术影响力，能够确保评审质量。</p>
        </div>
        <div class="p-4 bg-slate-50 rounded-xl">
          <h4 class="font-semibold text-slate-700 text-sm mb-2">合规性审查</h4>
          <p class="text-xs text-slate-500 leading-relaxed">已通过政治立场、科研诚信、回避原则等合规性审查，确保评审公正性。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExpertSelection',
  data() {
    return {
      coreExperts: [
        { name: '张明华', title: '教授', institution: '清华大学', field: '人工智能、机器学习', match: 95, projects: 12, selected: true },
        { name: '李晓峰', title: '研究员', institution: '中科院', field: '计算机视觉、深度学习', match: 92, projects: 8, selected: true },
        { name: '王建国', title: '教授', institution: '北京大学', field: '自然语言处理', match: 90, projects: 15, selected: false }
      ],
      edgeExperts: [
        { name: '陈志强', title: '副教授', institution: '浙江大学', field: '智能系统、机器人', match: 58, projects: 6, selected: false },
        { name: '刘芳', title: '研究员', institution: '复旦大学', field: '数据挖掘、知识图谱', match: 55, projects: 9, selected: false },
        { name: '赵伟', title: '教授', institution: '南京大学', field: '模式识别、图像处理', match: 52, projects: 11, selected: false }
      ]
    }
  },
  computed: {
    selectedCount() {
      return [...this.coreExperts, ...this.edgeExperts].filter(e => e.selected).length
    }
  }
}
</script>
